import type { Patient, Practitioner } from '@medplum/fhirtypes';
import { US_NPI_SYSTEM } from '@bariendo/lib/constants';

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates that a Practitioner has a valid NPI identifier required by HealthGorilla
 */
export function validateProviderProfile(provider: Practitioner | undefined): ValidationResult {
  if (!provider) {
    return { valid: false, error: 'Provider is required' };
  }

  const npiIdentifier = provider.identifier?.find((id) => id.system === US_NPI_SYSTEM);

  if (!npiIdentifier?.value) {
    return {
      valid: false,
      error: `Practitioner must have an NPI identifier (${US_NPI_SYSTEM})`,
    };
  }

  return { valid: true };
}

/**
 * Validates that a Patient has required demographics for HealthGorilla
 */
export function validatePatientProfile(patient: Patient | undefined): ValidationResult {
  if (!patient) {
    return { valid: false, error: 'Patient is required' };
  }

  // Check for required demographics
  const hasName = patient.name && patient.name.length > 0;
  const hasBirthDate = !!patient.birthDate;
  const hasGender = !!patient.gender;

  if (!hasName || !hasBirthDate || !hasGender) {
    const missing = [];
    if (!hasName) missing.push('name');
    if (!hasBirthDate) missing.push('birthDate');
    if (!hasGender) missing.push('gender');
    return {
      valid: false,
      error: `Patient is missing required fields: ${missing.join(', ')}`,
    };
  }

  return { valid: true };
}

/**
 * Formats Health Gorilla integration errors into user-friendly messages
 * Similar to DoseSpot error handling but using alert.error format
 */
export function formatHealthGorillaError(error: unknown): string {
  if (!(error instanceof Error)) {
    return 'An unexpected error occurred when creating the lab order. Please contact an Orbit admin if the problem persists.';
  }

  // Try to parse JSON error message if it exists
  let errorMessage = error.message;
  try {
    const parsed = JSON.parse(error.message);
    if (parsed.errorMessage) {
      errorMessage = parsed.errorMessage;
    }
  } catch {
    // Not valid JSON, continue with original error message
  }

  // eslint-disable-next-line no-console
  console.error('Health Gorilla error:', errorMessage);

  if (
    errorMessage.includes('patient') ||
    errorMessage.includes('practitioner') ||
    errorMessage.includes('provider') ||
    errorMessage.includes('npi')
  ) {
    return `Couldn't create lab order. Please verify patient data is complete.
      Please contact an Orbit admin if the problem persists.`;
  }

  return `An unexpected error occurred when syncing patient data with Health Gorilla.
    Please contact an Orbit admin if the problem persists.`;
}
